/**
 * Merges two arrays with the option
 * to allow `n` items from the first array before
 * inserting an item from the second array.
 *
 * @example
 * alternate([1, 2, 3, 4], [5, 6, 7, 8]) // [1, 5, 2, 6, 3, 7, 4, 8]
 * alternate([1, 2, 3, 4], [5, 6, 7, 8], \{ every: 2 \}) // [1, 2, 6, 3, 4, 8]
 */
export function alternate<A, B>(as: A[], bs: B[], options?: { every: number }): (A | B)[] {
  const step = options?.every ?? 1;

  return as.reduce<(A | B)[]>((result, a, index) => {
    result.push(a);

    const indexMatches = (index + 1) % step === 0;

    if (indexMatches) {
      const target = bs[Math.floor(index / step)];
      if (target) result.push(target);
    }

    return result;
  }, []);
}

/**
 * Creates an array containing groups of up to `count` length
 * from the provided array.
 *
 * @example
 * group(3, [1, 2, 3, 4, 5, 6, 7]) // [[1, 2, 3], [4, 5, 6], [7]]
 */
export function group<T>(count: number, xs: T[]): T[][] {
  const result: T[][] = [];

  xs.forEach((x) => {
    if (result.length === 0 || result[result.length - 1]?.length === count) {
      result.push([]);
    }

    result[result.length - 1]?.push(x);
  });

  return result;
}
